import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { PERMISSION_LEVELS, MODULES, ROLES, normalizeRole, OFFICIAL_EMAILS } from '../constants';

const isSuperAdminEmail = (email) => {
  if (!email) return false;
  const em = email.trim().toLowerCase();
  return OFFICIAL_EMAILS.superAdmins.some((e) => e.toLowerCase() === em);
};

/**
 * usePermissions — centralized hook for fine-grained module access control,
 * Super Admin / Admin hierarchy rules, and season permissions.
 */
export const usePermissions = () => {
  const { user, role, isAdmin, isSuperAdmin, isManager, permissions } = useAuth();

  const userEmail = useMemo(() => user?.email?.trim().toLowerCase() || '', [user]);

  // Robust normalized role resolution
  const normalizedUserRole = useMemo(() => {
    if (isSuperAdminEmail(userEmail)) return ROLES.superAdmin;
    return normalizeRole(role);
  }, [role, userEmail]);

  const resolvedIsSuperAdmin = useMemo(() => {
    return Boolean(isSuperAdmin || normalizedUserRole === ROLES.superAdmin || isSuperAdminEmail(userEmail));
  }, [isSuperAdmin, normalizedUserRole, userEmail]);

  const resolvedIsAdmin = useMemo(() => {
    return Boolean(resolvedIsSuperAdmin || isAdmin || normalizedUserRole === ROLES.admin);
  }, [resolvedIsSuperAdmin, isAdmin, normalizedUserRole]);

  const resolvedIsManager = useMemo(() => {
    return Boolean(resolvedIsAdmin || isManager || normalizedUserRole === ROLES.manager);
  }, [resolvedIsAdmin, isManager, normalizedUserRole]);

  /**
   * Check if user has at least read or write access to a module.
   * Admin always has access to all modules.
   */
  const canAccess = (moduleId) => {
    if (resolvedIsAdmin) return true;
    if (moduleId === MODULES.team) return false; // Only Admin / Super Admin can access team management
    const level = permissions?.[moduleId] || PERMISSION_LEVELS.none;
    return level === PERMISSION_LEVELS.read || level === PERMISSION_LEVELS.write;
  };

  /**
   * Check if user has read permission on a module.
   */
  const canRead = (moduleId) => {
    if (resolvedIsAdmin) return true;
    const level = permissions?.[moduleId] || PERMISSION_LEVELS.none;
    return level === PERMISSION_LEVELS.read || level === PERMISSION_LEVELS.write;
  };

  /**
   * Check if user has write (full control) permission on a module.
   */
  const canWrite = (moduleId) => {
    if (resolvedIsAdmin) return true;
    if (moduleId === MODULES.team) return false;
    const level = permissions?.[moduleId] || PERMISSION_LEVELS.none;
    return level === PERMISSION_LEVELS.write;
  };

  /**
   * Hierarchy check: Can current user edit or assign target member's role?
   * Only Super Admins can edit/promote/demote Admins or Super Admins.
   */
  const canEditTeamMember = (targetMember) => {
    if (!resolvedIsAdmin) return false;
    if (resolvedIsSuperAdmin) return true;
    const targetRole = normalizeRole(targetMember?.role);
    const targetIsAdmin =
      targetMember?.isSuperAdmin ||
      targetRole === ROLES.admin ||
      targetRole === ROLES.superAdmin ||
      isSuperAdminEmail(targetMember?.email);
    // Regular Admin cannot edit an Admin or Super Admin
    return !targetIsAdmin;
  };

  /**
   * Hierarchy check: Can current user remove target member?
   * Only Super Admins can remove other Admins.
   * Super Admins cannot remove themselves.
   */
  const canRemoveTeamMember = (targetMember) => {
    if (!resolvedIsAdmin || !targetMember) return false;
    const isTargetSelf = targetMember.email?.toLowerCase() === userEmail || (user?.uid && targetMember.uid === user.uid);
    if (isTargetSelf) return false; // Cannot remove self
    if (resolvedIsSuperAdmin) return true;
    const targetRole = normalizeRole(targetMember?.role);
    const targetIsAdmin =
      targetMember?.isSuperAdmin ||
      targetRole === ROLES.admin ||
      targetRole === ROLES.superAdmin ||
      isSuperAdminEmail(targetMember?.email);
    // Regular Admin cannot remove an Admin or Super Admin
    return !targetIsAdmin;
  };

  /**
   * Check if user is the Owner of a specific season.
   */
  const isSeasonOwner = (season) => {
    if (resolvedIsAdmin) return true;
    if (!season || !userEmail) return false;
    if (
      season.created_by === user?.uid ||
      season.host_email?.toLowerCase() === userEmail ||
      season.hostEmail?.toLowerCase() === userEmail ||
      season.creator_email?.toLowerCase() === userEmail ||
      season.user_email?.toLowerCase() === userEmail
    ) {
      return true;
    }
    const directRole = season.access_control?.[userEmail] || season.access_control?.[user?.email];
    return directRole === 'Owner';
  };

  /**
   * Check if user has editing (Owner or Contributor) rights on a specific season.
   */
  const isSeasonEditor = (season) => {
    if (resolvedIsAdmin) return true;
    if (!season || !userEmail) return false;
    if (isSeasonOwner(season)) return true;
    const directRole = season.access_control?.[userEmail] || season.access_control?.[user?.email];
    return directRole === 'Owner' || directRole === 'Contributor';
  };

  /**
   * Check if user is restricted to Read-Only on a specific season.
   */
  const isSeasonReader = (season) => {
    if (resolvedIsAdmin) return false;
    if (!season || !userEmail) return false;
    if (isSeasonEditor(season)) return false;
    const directRole = season.access_control?.[userEmail] || season.access_control?.[user?.email];
    return directRole === 'Reader';
  };

  /**
   * Get the computed display role label for a specific season.
   */
  const getSeasonRole = (season) => {
    if (resolvedIsSuperAdmin) return 'Super Admin';
    if (resolvedIsAdmin) return 'Admin';
    if (!season || !userEmail) return 'Reader';
    if (isSeasonOwner(season)) return 'Owner';
    const directRole = season.access_control?.[userEmail] || season.access_control?.[user?.email];
    if (directRole) return directRole;
    return 'Owner'; // fallback for user's own created seasons
  };

  return {
    user,
    userEmail,
    role: normalizedUserRole,
    isAdmin: resolvedIsAdmin,
    isSuperAdmin: resolvedIsSuperAdmin,
    isManager: resolvedIsManager,
    isManagerOrAbove: resolvedIsManager,
    permissions,
    canAccess,
    canRead,
    canWrite,
    canEditTeamMember,
    canRemoveTeamMember,
    isSeasonOwner,
    isSeasonEditor,
    isSeasonReader,
    getSeasonRole,
  };
};

export default usePermissions;
